import type { TimelineItemKind } from '@norum/contracts';

/**
 * The working day, as slots (docs/frontend/handoff, `data/day.js`).
 *
 * One row per hour between the day's bounds; a committed item occupies its
 * own row at its own minute, so two things in one hour are two rows and
 * never one hidden behind the other. An empty hour reads "Free". Only today
 * has a "now": other days are plans or records.
 *
 * Pure, so it is tested without a browser. It renders what `/v1/today`
 * returned and invents nothing: every slot that is not free carries the
 * item it came from, and the item carries `placedBy` and its reason.
 */

export type Span = 'day' | 'week' | 'last2' | 'next2';

/** A Today item as the wire carries it: dates as ISO strings. */
export interface WireItem {
  kind: TimelineItemKind;
  sourceId: string | null;
  at: string | null;
  until: string | null;
  durationMinutes: number | null;
  rank: number | null;
  pinned: boolean;
  label: string | null;
  placedBy: 'candidate' | 'human' | 'agent';
  reason: string | null;
  /** The agent that placed it, as it acted; absent from an older API. */
  actor?: string | null;
  /** That agent's given name — "Sol placed it". */
  actorName?: string | null;
  /** What started an agent's placement (ADR-0058). */
  origin?: 'chat' | 'confirmation' | 'assignment' | 'schedule' | null;
}

export type SlotState = 'past' | 'live' | 'next' | 'ahead';

export interface ItemSlot {
  kind: 'item';
  /** Minutes since midnight, local to the day. Also the slot's identity. */
  startMin: number;
  label: string;
  state: SlotState;
  item: WireItem;
  durationMin: number;
}

export interface FreeSlot {
  kind: 'free';
  startMin: number;
  label: string;
  state: 'past' | 'ahead';
}

export type Slot = ItemSlot | FreeSlot;

export interface DayOptions {
  /** The day the grid shows, at local midnight. */
  day: Date;
  /** The clock. Only a day that contains it has a live and a next slot. */
  now: Date;
  /** Bounds, in hours. Settings; the prototype's default is 07:00–19:00. */
  dayStart?: number;
  dayEnd?: number;
}

const pad = (n: number): string => String(n).padStart(2, '0');
export const minuteLabel = (min: number): string => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Minutes since local midnight of `day`, or null when the item has no time. */
function minuteOf(item: WireItem, day: Date): number | null {
  if (!item.at) return null;
  const at = new Date(item.at);
  if (!sameDay(at, day)) return null;
  return at.getHours() * 60 + at.getMinutes();
}

/** How long an item holds the row. The candidate's own span first, then the plan's, then an hour. */
export function durationOf(item: WireItem): number {
  if (item.durationMinutes) return item.durationMinutes;
  if (item.at && item.until) {
    const span = (new Date(item.until).getTime() - new Date(item.at).getTime()) / 60_000;
    if (span > 0) return Math.round(span);
  }
  return 60;
}

/** Items on this day with a time, in their hourly rows; and the ones with none. */
export function buildDay(
  items: readonly WireItem[],
  options: DayOptions,
): { slots: Slot[]; untimed: WireItem[]; isToday: boolean } {
  const start = options.dayStart ?? 7;
  const end = options.dayEnd ?? 19;
  const day = startOfDay(options.day);
  const isToday = sameDay(day, options.now);
  const nowMin = options.now.getHours() * 60 + options.now.getMinutes();

  const timed: Array<{ item: WireItem; startMin: number }> = [];
  const untimed: WireItem[] = [];
  for (const item of items) {
    const min = minuteOf(item, day);
    if (min === null) {
      if (!item.at) untimed.push(item);
      continue;
    }
    timed.push({ item, startMin: min });
  }
  timed.sort((a, b) => a.startMin - b.startMin || (a.item.rank ?? 0) - (b.item.rank ?? 0));

  const slots: Slot[] = [];
  // An item before the day's first hour or after its last still shows: the
  // bounds are where free hours are drawn, not a filter on what happened.
  const firstHour = Math.min(start, ...timed.map((t) => Math.floor(t.startMin / 60)));
  const lastHour = Math.max(end, ...timed.map((t) => Math.floor(t.startMin / 60)));

  for (let hour = firstHour; hour <= lastHour; hour += 1) {
    const inHour = timed.filter((t) => Math.floor(t.startMin / 60) === hour);
    if (inHour.length > 0) {
      for (const { item, startMin } of inHour) {
        const durationMin = durationOf(item);
        const live = isToday && nowMin >= startMin && nowMin < startMin + durationMin;
        const past = isToday ? startMin + durationMin <= nowMin && !live : day < startOfDay(options.now);
        slots.push({
          kind: 'item',
          startMin,
          label: minuteLabel(startMin),
          state: live ? 'live' : past ? 'past' : 'ahead',
          item,
          durationMin,
        });
      }
    } else {
      const past = isToday ? hour * 60 + 60 <= nowMin : day < startOfDay(options.now);
      slots.push({ kind: 'free', startMin: hour * 60, label: minuteLabel(hour * 60), state: past ? 'past' : 'ahead' });
    }
  }

  // The next item is the first committed slot still ahead — the clock
  // decides, and only when nothing is live.
  if (isToday && !slots.some((s) => s.kind === 'item' && s.state === 'live')) {
    const next = slots.find((s): s is ItemSlot => s.kind === 'item' && s.state === 'ahead');
    if (next) next.state = 'next';
  }

  return { slots, untimed, isToday };
}

/** Items grouped by local day, for the ranges that are a list rather than a grid. */
export function groupByDay(
  items: readonly WireItem[],
  from: Date,
  to: Date,
): Array<{ day: Date; items: WireItem[] }> {
  const days: Array<{ day: Date; items: WireItem[] }> = [];
  for (let d = startOfDay(from); d < to; d = addDays(d, 1)) {
    days.push({
      day: d,
      items: items
        .filter((i) => i.at !== null && sameDay(new Date(i.at), d))
        .sort((a, b) => new Date(a.at!).getTime() - new Date(b.at!).getTime()),
    });
  }
  return days;
}

/**
 * The prototype's `month` is a fifth horizon the platform does not have:
 * `/v1/today` answers `day`, `week`, `last2` and `next2` (§19). A month is
 * shown as the next four weeks, asked for one week at a time, and the
 * screen says "proposed" beside it rather than pretending the range exists.
 */
export function weekAnchors(from: Date): Date[] {
  return [0, 7, 14, 21].map((n) => addDays(startOfDay(from), n));
}
